import { handleActions } from 'redux-actions';
import * as dateFns from 'date-fns';
import {
  requestFail,
  reservationsForDay,
  reservationsForPrevDays,
  changeCurrentDate,
  confirmedForDay,
  didAttend,
  didNotAttend,
  getTimetableSuccess,
  getScheduleSuccess,
  resetScheduleSuccess,
  changeDay,
  changeMonth,
  getCalendarSuccess,
  filterCalendarSuccess,
  changeConfigurationDateSuccess,
  createResourceSuccess,
  newUsersRegistered,
  editDonorSuccess,
  donorDonationSiteChanged,
  editNotChecked,
  refreshConfiguration,
  setCurrentAppTypeFilter,
  setCurrentBloodTypeFilter,
  setCurrentDonorStatusFilter,
  setCurrentTiterFilter,
  fetchingConfiguration,
  fetchingDay,
  fetchingMonth,
  fetchingSidebar,
  fetchingSidebarAborted,
  fetchingDayAborted,
  fetchingMonthAborted,
  setRefreshed,
  changeReservationSuccess,
  setNotRefreshed,
  setConfigurationRefreshed,
  deleteResourceSuccess,
  editResourceSuccess,
  setResourceMenuIsOpen,
  setSelectedResource,
  setIsResourceEmpty,
  toggleDeleteResourceModal,
  toggleEditResourceModal,
  resetDailySchedule,
  overwritingTimeslot,
  toggleOverwriteModal, setForceRefreshed, setNotForceRefreshed,
  setIsDirectlyBooking,
} from './actions';
// Cross-domain action type (string constant only — importing the auth action
// creators would drag firebase into this module).
import { LOGIN_SUCCESS } from '../auth/actionTypes';

const initialState = {
  dailyReservations: [],
  prevDaysReservations: [],
  dailySchedule: [],
  timetable: [],
  isError: false,
  error: null,
  currentDate: dateFns.startOfDay(new Date()),
  confirmed: [],
  currentMonth: dateFns.startOfMonth(new Date()).getTime() - new Date().getTimezoneOffset() * 60000,
  currentDay: dateFns.startOfDay(new Date()).getTime(),
  calendar: [],
  calendarIsFetched: false,
  configurationDate: dateFns.startOfDay(new Date()),
  needRefresh: false,
  forceRefresh: false,
  needRefreshConfiguration: false,
  newUsers: [],
  appTypeFilter: 'All',
  bloodTypeFilter: 'all',
  donorStatusFilter: 'all',
  titerFilter: 'all',
  isSidebarFetching: false,
  isDayViewFetching: false,
  isMonthViewFetching: false,
  isConfigurationFetching: false,
  // ownership tokens for the four fetching flags (see helpers below):
  // the flightId of the request that currently owns the flag, or null
  sidebarFetchingOwner: null,
  dayViewFetchingOwner: null,
  monthViewFetchingOwner: null,
  configurationFetchingOwner: null,
  timetableIsFetched: false,
  dayIsFetched: false,
  reservationsIsFetched: false,
  isToastSuccessShown: false,
  isToastErrorShown: false,
  resourceMenuIsOpened: false,
  selectedResource: null,
  isResourceEmpty: null,
  resourceDeleteModalIsOpened: false,
  resourceEditModalIsOpened: false,
  isTimeslotOverwriting: false,
  overwritingError: null,
  isOverwriteModalOpened:false,
  isDirectlyBooking: false,
  // dailySchedule belongs to a DAY; true from the moment currentDay moves to
  // a different day until a /schedule result (or any request failure) lands.
  // Not a fetch flag (no owner): the day-view loader gate reads it so the
  // stale previous day's appointments never render onto the new day's grid
  // while the (silent, listener-driven) schedule fetch is still pending.
  isDailyScheduleStale: false,
};

// ─── fetch-flag ownership (compare-and-clear) ────────────────────────────
// The four is*Fetching flags stay plain booleans for every consumer, but each
// carries a companion owner token: the flightId (from actions.js) of the
// request that set it. Setting the flag takes ownership (a successor
// OVERWRITES the owner — it steals the flag); releasing it is
// compare-and-clear: only the current owner's release clears the flag, a
// release from a dead predecessor (aborted by addPendingRequest, superseded,
// or failed late) is a no-op. This is ownership, NOT refcounting — there is
// exactly one owner, and the invariant is: flag true ⇒ the owner's request is
// still unsettled ⇒ exactly one owned release is still coming.
//
// Interaction cases this encodes:
// - same-key abort: predecessor's fetching*Aborted / requestFail carries the
//   predecessor's id, successor owns the flag → no-op (no early blink).
// - silent refresh (isLoaderNotNeeded / disableLoader): the flight never set
//   the flag, so it never owns it → its releases no-op against a live owner.
// - falsy-input guards (f076381): no flag set → no ownership → nothing to
//   release; the guards and ownership are independent protections.
// - a8a9797 month guard: unchanged and composes — the requestId/month checks
//   protect the DATA, the owner token protects the FLAG lifetime.
//
// owner === null with the flag true means a legacy id-less set (today only
// Configuration.jsx's direct fetchingConfiguration() dispatch): such an
// "ownerless" flag keeps the pre-ownership behavior — ANY release may clear
// it, otherwise it could never be released at all.
const FLAG_OWNER = {
  isSidebarFetching: 'sidebarFetchingOwner',
  isDayViewFetching: 'dayViewFetchingOwner',
  isMonthViewFetching: 'monthViewFetchingOwner',
  isConfigurationFetching: 'configurationFetchingOwner',
};

const normalizeId = (requestId) => (requestId === undefined ? null : requestId);

// state patch that sets `flag` and records the setter as its owner
const setFlag = (flag, requestId) => ({
  [flag]: true,
  [FLAG_OWNER[flag]]: normalizeId(requestId),
});

// state patch that clears `flag` ONLY if `requestId` owns it (or the flag is
// ownerless); a stale release returns an empty patch — a no-op
const releaseFlag = (state, flag, requestId) => {
  const ownerKey = FLAG_OWNER[flag];
  // normalize the stored owner too: a state slice that somehow lacks the
  // owner field (undefined) degrades to "ownerless", never to "unreleasable"
  const owner = normalizeId(state[ownerKey]);
  if (owner !== null && owner !== normalizeId(requestId)) {
    return {};
  }
  return { [flag]: false, [ownerKey]: null };
};

const metaId = (action) => (action && action.meta ? action.meta.requestId : undefined);

const reservationReducer = handleActions({
  // Auth (re)established — fresh login, MFA finalize, or session-restore
  // rehydrate. Any error surface still set at this point belongs to a
  // PREVIOUS auth state (typically the unauthenticated 401 burst fired at
  // app boot from stale persisted state), and Schedule's mount effect would
  // otherwise toast "Something went wrong!" right after a successful sign-in.
  // Only the error surface is reset: fetch flags/owners belong to in-flight
  // requests and are settled by their own owner-checked releases.
  [LOGIN_SUCCESS]: (state) => ({
    ...state,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [requestFail]: (state, action) => {
    const suppress = action.payload && action.payload._suppressToast;
    // a failed fetch must release the flag(s) it set: Dashboard's
    // reservations-listener gate requires all four flags false, so a stranded
    // flag blocked day/month data loading forever. But the release is
    // owner-checked per flag — REQUEST_FAIL is a shared action type
    // (reservations, users, logging, pushMessages all dispatch it), and an
    // unrelated failure must no longer blank a flag whose owning request is
    // still in flight (that in-flight owner always settles its own flag).
    const id = metaId(action);
    return {
      ...state,
      isError: !suppress,
      error: suppress ? null : action.payload,
      isToastErrorShown: !suppress,
      // any failure drops the stale-schedule hold (not owner-scoped on
      // purpose: erring towards an empty grid + toast, never a stuck loader)
      isDailyScheduleStale: false,
      ...releaseFlag(state, 'isDayViewFetching', id),
      ...releaseFlag(state, 'isMonthViewFetching', id),
      ...releaseFlag(state, 'isSidebarFetching', id),
      ...releaseFlag(state, 'isConfigurationFetching', id),
    };
  },
  [reservationsForDay]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    reservationsIsFetched: true,
    dailyReservations: action?.payload?.content,
    needRefresh: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [reservationsForPrevDays]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    reservationsIsFetched: true,
    prevDaysReservations: action?.payload?.content,
    needRefresh: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [overwritingTimeslot]: (state, action) => ({
    ...state,
    ...action.payload,
  }),
  [toggleOverwriteModal]:(state)=>({
    ...state,
    isOverwriteModalOpened: !state.isOverwriteModalOpened,
  }),
  [changeCurrentDate]: (state, action) => ({
    ...state,
    currentDate: action.payload,
  }),
  [changeReservationSuccess]: (state, action) =>({
    ...state,
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    needRefresh: true,
    forceRefresh: true,
    isToastErrorShown: false,
    isToastSuccessShown: true,
    isDirectlyBooking: false,
  }),
  [didNotAttend]: (state, action) =>({
    ...state,
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    needRefresh: true,
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  [didAttend]: (state, action) =>({
    ...state,
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    needRefresh: true,
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  [getTimetableSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isMonthViewFetching', metaId(action)),
    timetableIsFetched: true,
    timetable: action?.payload?.content,
    isToastErrorShown: false,
  }),
  [getScheduleSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    dailySchedule: action.payload?.content,
    isDailyScheduleStale: false,
    needRefresh: false,
    isToastErrorShown: false,
  }),
  [resetDailySchedule]:(state) => ({
    ...state,
    dailySchedule: [],
  }),
  [resetScheduleSuccess]: (state, action) => ({
    ...state,
    appTypeFilter: 'All',
    bloodTypeFilter: 'all',
    donorStatusFilter: 'all',
    titerFilter: 'all',
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    needRefresh: true,
    isToastErrorShown: false,
  }),
  [changeMonth]: (state, action) => ({
    ...state,
    currentMonth: action.payload,
  }),
  [changeDay]: (state, action) => {
    if (action.payload === state.currentDay) return state;
    return {
      ...state,
      currentDay: action.payload,
      // the schedule on screen is the previous day's: drop it and hold the
      // day-view loader until the new day's /schedule result arrives
      dailySchedule: [],
      isDailyScheduleStale: true,
    };
  },
  [getCalendarSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isConfigurationFetching', metaId(action)),
    calendar: action.payload?.content,
    calendarIsFetched: true,
    needRefreshConfiguration: false,
    isToastErrorShown: false,
  }),
  [filterCalendarSuccess]: (state, action) => ({
    ...state,
    calendar: action.payload?.content,
  }),
  [changeConfigurationDateSuccess]: (state, action) => ({
    ...state,
    // no owning flight here (a date change, not a fetch): this clears only an
    // OWNERLESS flag, exactly like before for the legacy Configuration.jsx
    // path; a flag owned by an in-flight fetch keeps its loader until that
    // fetch settles it
    ...releaseFlag(state, 'isConfigurationFetching', metaId(action)),
    configurationDate: action.payload,
  }),
  [createResourceSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isConfigurationFetching', metaId(action)),
    calendar: [...state.calendar, action.payload.resource],
    dailySchedule: [...state.dailySchedule, action.payload.resource],
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  [deleteResourceSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isConfigurationFetching', metaId(action)),
    calendar: [...state.calendar.filter((item) => item.resourceId !== action.payload?.content)],
    dailySchedule: [...state.dailySchedule.filter((item) => item.resourceId !== action.payload?.content)],
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  [editResourceSuccess]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isConfigurationFetching', metaId(action)),
    // calendar: [...state.calendar, action.payload.resource],
    // dailySchedule: [...state.dailySchedule, action.payload.resource],
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  [newUsersRegistered]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    newUsers: action.payload?.content,
    needRefresh: false,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [editDonorSuccess]: (state, action) =>({
    ...state,
    ...releaseFlag(state, 'isSidebarFetching', metaId(action)),
    ...releaseFlag(state, 'isDayViewFetching', metaId(action)),
    needRefresh: true,
    isToastErrorShown: false,
    isToastSuccessShown: true
  }),
  // CMT-57: a moved donor left this bank — reload what is on screen (the
  // day view's sidebar / new registrations, the donor list's current page).
  // No fetch flag was set for the move, so nothing to release.
  [donorDonationSiteChanged]: (state, action) => (
    action.payload && action.payload.moved
      ? { ...state, needRefresh: true, isToastErrorShown: false }
      : state
  ),
  [refreshConfiguration]: (state) => ({
    ...state,
    needRefreshConfiguration: true,
  }),
  [setConfigurationRefreshed]: (state) => ({
    ...state,
    needRefreshConfiguration: false,
  }),
  [setCurrentAppTypeFilter]: (state, action) => ({
    ...state,
    appTypeFilter: action.payload,
  }),
  [setCurrentBloodTypeFilter]: (state, action) => ({
    ...state,
    bloodTypeFilter: action.payload,
  }),
  [setCurrentDonorStatusFilter]: (state, action) => ({
    ...state,
    donorStatusFilter: action.payload,
  }),
  [setCurrentTiterFilter]: (state, action) => ({
    ...state,
    titerFilter: action.payload,
  }),
  // SET actions: payload is the setter's flightId — setting takes ownership
  // (a successor overwrites a predecessor's token)
  [fetchingSidebar]: (state, action) => ({
    ...state,
    ...setFlag('isSidebarFetching', action.payload),
    isToastSuccessShown: false
  }),
  [fetchingConfiguration]: (state, action) => ({
    ...state,
    ...setFlag('isConfigurationFetching', action.payload),
    isToastSuccessShown: false
  }),
  [fetchingDay]: (state, action) => ({
    ...state,
    ...setFlag('isDayViewFetching', action.payload),
    isToastSuccessShown: false
  }),
  [fetchingMonth]: (state, action) => ({
    ...state,
    ...setFlag('isMonthViewFetching', action.payload),
    isToastSuccessShown: false
  }),
  // ABORT releases: payload is the aborted flight's id — compare-and-clear,
  // so a predecessor aborted by its same-key successor cannot blank the flag
  // the successor just set
  [fetchingSidebarAborted]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isSidebarFetching', action.payload),
  }),
  [fetchingDayAborted]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isDayViewFetching', action.payload),
  }),
  [fetchingMonthAborted]: (state, action) => ({
    ...state,
    ...releaseFlag(state, 'isMonthViewFetching', action.payload),
  }),
  [setRefreshed]: (state) => ({
    ...state,
    needRefresh: false,
  }),
  [setNotRefreshed]: (state) => ({
    ...state,
    needRefresh: true,
  }),
  [setForceRefreshed]: (state) => ({
    ...state,
    forceRefresh: false,
  }),
  [setNotForceRefreshed]: (state) => ({
    ...state,
    forceRefresh: true,
  }),
  [setResourceMenuIsOpen]: (state) => ({
    ...state,
    resourceMenuIsOpened: true,
  }),
  [setSelectedResource]: (state, action) => ({
    ...state,
    selectedResource: action.payload,
  }),
  [setIsResourceEmpty]: (state, action) => ({
    ...state,
    isResourceEmpty: action.payload,
  }),
  [toggleDeleteResourceModal]: (state) => ({
    ...state,
    resourceDeleteModalIsOpened: !state.resourceDeleteModalIsOpened,
  }),
  [toggleEditResourceModal]: (state) => ({
    ...state,
    resourceEditModalIsOpened: !state.resourceEditModalIsOpened,
  }),
  [setIsDirectlyBooking]: (state, action) => ({
    ...state,
    isDirectlyBooking: action.payload,
  }),
},
initialState);

export default reservationReducer;
